import { getCustomerCompanyListQueryOptions } from "@boundless/bl-api-client/customer/services"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { z } from "zod"

import { companyListQueryOptions, isCurrentUserOwner } from "@/lib/companies"

/**
 * Guard and company resolution for every Users page.
 *
 * Held once here so the members table and the activity trail cannot drift
 * apart on who may see them or on which company they are scoped to.
 */
export const Route = createFileRoute("/_dashboard/_customer/users")({
  validateSearch: z.object({
    company: z.string().optional(),
  }),
  beforeLoad: async ({
    search,
    context: { store, queryClient, hasPermissions },
  }) => {
    if (!hasPermissions(["customer.view_membership"])) {
      throw redirect({ to: "/" })
    }

    // Same cache key `CompanyWrapper` populates, and awaiting it here also
    // resolves `activeCompanyUuid` on a cold load. The shared options are
    // mandatory, not a convenience - see `companyListQueryOptions`.
    const companies = await queryClient.ensureQueryData(
      getCustomerCompanyListQueryOptions({}, companyListQueryOptions()),
    )

    // The header switcher re-scopes the whole dashboard and lands on `/resume`.
    // Here the company is a filter on these pages only, held in the URL so it
    // survives a reload without touching the active company.
    // `||`, not `??`: the param is user-editable, and a blank `?company=`
    // should fall back to the active company rather than count as a choice.
    const companyUuid = search.company || store.getState().activeCompanyUuid
    if (!companyUuid) {
      throw redirect({ to: "/home" })
    }

    // Owner-only end to end: every endpoint these pages read 403s for a
    // Collaborator, so there is nothing left to render. Bouncing to the role
    // dispatcher cannot loop, because it never lands back here.
    if (!isCurrentUserOwner(companies.find((c) => c.uuid === companyUuid))) {
      throw redirect({ to: "/" })
    }

    return { companyUuid }
  },
})
